import HeightFillingImage from 'components/Common/HeightFillingImage'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import Divider from 'LuxKit/Divider'
import type { OfficeLocations } from '../OfficeLocationModal/OfficeLocationModal'
import OfficeLocationModal, { OFFICE_LOCATION_CONTENT } from '../OfficeLocationModal/OfficeLocationModal'

const StyledLayoutContainer = styled(LayoutContainer)`
  display: grid;
  grid-template-areas:
    "video"
    "body";
  gap: ${rem(40)};
  padding: ${rem(40)} ${rem(20)} ${rem(40)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} ${rem(16)} ${rem(40)} ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-areas:
    "body video";
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(40)} unset ${rem(40)} unset;
  }
`

const OfficeContainer = styled(Group)`
  column-gap: ${rem(12)};
`

const VideoContainer = styled(Group)`
   grid-area: video;
`

const BodyContainer = styled(Group)`
  grid-area: body;
`

const StyledDivider = styled(Divider)`
  && {
    height: unset; // needed for when list of offices wraps, otherwise height doubles
  }
`

const PlayButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(-50%) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(48)};
  width: ${rem(48)};

  ${mediaQueryDown.mobile} {
    height: ${rem(36)};
    width: ${rem(36)};
  }
`

const StyledPlayIcon = styled(SolidPlayIcon)`
  height: ${rem(24)};
  width: ${rem(24)};

  ${mediaQueryDown.mobile} {
    height: ${rem(20)};
    width: ${rem(20)};
  }
`

const VimeoWrapper = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};
  height: ${rem(218)};
  width: 90vw;

  ${mediaQueryUp.tablet} {
    height: unset;
    width: unset;
  }

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(-50%) scale(1.2);
    }
  }

`

function OurStory() {
  const intl = useIntl()
  const { offices } = OFFICE_LOCATION_CONTENT
  const showLightbox = useContext(ModalContext)
  const showModal = useContext(ModalContext)
  const toggleModal = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const selectedOfficeLocation = e.currentTarget.dataset.key as OfficeLocations
    showModal(<OfficeLocationModal selectedOffice={selectedOfficeLocation} />)
  }, [showModal])

  const ourStoryHeading = intl.formatMessage({
    id: 'BjKG2moeWa',
    defaultMessage: 'Our story',
    description: 'About us page - our story section heading',
  })

  const ourStoryBody = intl.formatMessage({
    id: 'LB58QeSUr+',
    defaultMessage: 'Founded in Melbourne, Australia in 2013 by award-winning entrepreneur Adam Schwab, who still serves as the CEO, Luxury Escapes began as a small start-up and has since evolved into the premier destination for exclusive, handpicked getaways. Boasting over 9 million global members, Luxury Escapes stands as one of the world\u2019s fastest-growing travel companies.',
    description: 'About us page - our story body text describing company history',
  })

  const ourLocationsLabel = intl.formatMessage({
    id: 'jxVvT5PU/e',
    defaultMessage: 'Our locations',
    description: 'About us page - our locations sub-heading',
  })

  return (
    <Pane>
      <StyledLayoutContainer>
        <BodyContainer direction="vertical" gap={32} verticalAlign="center">
          <Group direction="vertical" gap={12}>
            <Heading variant="heading2">{ourStoryHeading}</Heading>
            <BodyText variant="large">
              {ourStoryBody}
            </BodyText>
          </Group>
          <Group direction="vertical" gap={4}>
            <BodyText variant="large" weight="bold">{ourLocationsLabel}</BodyText>
            <OfficeContainer direction="horizontal" wrap="wrap">
              {offices.map((office, index) => <>
                <TextLink
                  key={office.location}
                  weight="regular"
                  onClick={toggleModal}
                  data-key={office.location}
                >
                  {office.location}
                </TextLink>
                {(offices.length !== index + 1) && <StyledDivider kind="primary" direction="vertical" />}
              </>)}
            </OfficeContainer>
          </Group>
        </BodyContainer>
        <VideoContainer direction="horizontal" horizontalAlign="center">
          <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId="804158238" playOnMount />)}>
            <HeightFillingImage id="a9f5kpytaomec09i8h08" minHeightAspectRatio={{ ratio: '3:1', desktopRatio: '3:2' }} width="660" />
            <PlayButton>
              <StyledPlayIcon />
            </PlayButton>
          </VimeoWrapper>
        </VideoContainer>
      </StyledLayoutContainer>
    </Pane>
  )
}

export default OurStory
